import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import AnalysisTemplateConfig from 'procedures/design/analysis-template-config/AnalysisTemplateConfig';
import ProcedureValidationErrorInfoButton from 'procedures/design/buttons/ProcedureValidationErrorInfoButton';
import {MethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import {MethodEditorTab} from 'procedures/design/method-config/MethodConfigConstants';
import MethodEditorContext from 'procedures/design/method-config/MethodEditorContext';
import {
  methodEditorHorizontalMargin,
  methodEditorVerticalMargin,
} from 'procedures/design/method-config/MethodEditorTabShared';
import MethodMetadataEditor from 'procedures/design/method-config/MethodMetadataEditor';
import TestTablesEditor from 'procedures/design/method-config/TestTablesEditor';
import {useLoadAllDocumentsInMethod} from 'procedures/design/method-config/useLoadAllDocumentsInMethod';
import useMethodEditorSidepanelPoW from 'procedures/design/method-config/useMethodEditorSidepanelPoW';
import StepConfigEditor, {StepGroupUpdater} from 'procedures/design/step-config/StepConfigEditor';
import useSelectedStep from 'procedures/design/utils/useSelectedStep';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  filterProcedureValidationErrors,
  findParameterTableValidationErrors,
  findRelevantProcedureValidationErrors,
} from 'procedures/design/validation/procedureValidationUtils';
import {trackReorderedProcedureStepEvent} from 'procedures/procedureAnalyticsEventsUtil';
import {Method, ProcedureStepConfig, ProcedureStepGroupType} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {GRAY, WHITE} from 'ui-library/Colors';
import {CenteredSpinner} from 'ui-library/Spinner';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import useUnsavedTabChangesCheck from '../useUnsavedTabChangesCheck';
import MethodDefinitionTables from './MethodDefinitionTables';

function MethodEditorTabTitle(props: {
  tabTitle: string;
  tabRelatedValidationErrors: Array<ProcedureValidationError>;
}): JSX.Element {
  return (
    <div
      className={css(styles.tabLabel)}
      {...getTestProps({component: 'MethodEditorTabTitle', element: 'root'})}
    >
      <ProcedureValidationErrorInfoButton preFilteredValidationErrors={props.tabRelatedValidationErrors} />
      {props.tabTitle}
    </div>
  );
}

export interface MethodEditorProps {
  allMethodNames: Array<{cid: string; name: string}>;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  isProcedureReadOnly: boolean;
  isProcedureArchived: boolean;
  // The following state is controlled by the parent because MethodEditor loses continuity of rendering
  // when the backend returns methods with different ids (because of immutable versioning)
  selectedMethod: Method;
}

export interface ReorderStepEvent {
  step: ProcedureStepConfig;
  stepGroupType: ProcedureStepGroupType;
  direction: number | 'drag';
}

export default function MethodEditor(props: MethodEditorProps): JSX.Element {
  const {onChangeMethod, selectedMethod} = props;
  const stepDocumentsLoaded = useLoadAllDocumentsInMethod(selectedMethod.cid, selectedMethod.stepGroups);
  const {
    isEditingMethodTab,
    setIsEditingMethodTab,
    procedureMetadata,
    setShouldPauseAutosave,
    currentTab: _currentTab,
    dangerouslySetCurrentTab: _dangerouslySetCurrentTab,
  } = ProcedurePageContext.useContext();
  const {id: procedureId, procedureType} = procedureMetadata;
  const {pauseAutosaver: customPauseAutosaver} = MethodSidepanelContext.useContext();
  const {selectedStepCid, onChangeSelectedStep} = useSelectedStep(selectedMethod);

  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const runUnsavedTabChangesCheck = useUnsavedTabChangesCheck(_currentTab);

  const {activeMethodEditorTab, changeToTab} = useMethodEditorSidepanelPoW(
    _currentTab,
    _dangerouslySetCurrentTab
  );

  const selectedMethodValidationErrors = findRelevantProcedureValidationErrors(procedureValidationErrors, {
    id: selectedMethod.cid,
    objectType: ProcedureValidationObjectType.METHOD,
  });
  const parameterTableValidationErrors = findParameterTableValidationErrors(
    selectedMethodValidationErrors,
    selectedMethod.cid
  );

  // Delegates to either the PoW or Legacy pauser
  const doPauseAutosaver = (newState: boolean): void => {
    if (customPauseAutosaver) {
      customPauseAutosaver(ProcedurePauseCondition.IS_EDITING_PARAMETERS, newState);
    } else {
      setShouldPauseAutosave(newState);
    }
  };

  const setCurrentTab = useCallback(
    (newTab: MethodEditorTab): void => {
      runUnsavedTabChangesCheck(async () => {
        await changeToTab(newTab);
      });
    },
    [changeToTab, runUnsavedTabChangesCheck]
  );

  const navigateToStepHandler = useCallback(
    (stepCid: string): void => {
      setCurrentTab(MethodEditorTab.STEP_CONFIG);
      onChangeSelectedStep(stepCid);
    },
    [setCurrentTab, onChangeSelectedStep]
  );

  const onReorderedStep = useCallback(
    (params: ReorderStepEvent): void => {
      const {id: methodId, cid: methodCid, nodeId: methodNodeId} = selectedMethod;
      const {step, direction} = params;

      trackReorderedProcedureStepEvent(
        procedureId,
        procedureType,
        methodNodeId,
        methodId,
        methodCid,
        step.id,
        step.cid,
        params.stepGroupType,
        direction === 'drag' ? 'drag' : direction === 1 ? 'down' : 'up'
      );
    },
    [procedureId, procedureType, selectedMethod]
  );

  const onChangeSteps = useCallback(
    (updater: StepGroupUpdater) => {
      onChangeMethod(selectedMethod.cid, (m) => ({...m, stepGroups: updater(m.stepGroups)}));
    },
    [onChangeMethod, selectedMethod]
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      doPauseAutosaver(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const allMethodNamesExcludingSelectedMethod = props.allMethodNames
    .filter(({cid}) => cid !== selectedMethod.cid)
    .map(({name}) => name);

  const methodEditorContextValue = useMemo(() => ({selectedStepCid}), [selectedStepCid]);

  // Do not render editor while step documents are loading, or if they failed to load
  if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS && !stepDocumentsLoaded) {
    return (
      <CenteredSpinner
        {...getTestProps({component: 'MethodEditor', element: 'step-documents-loading-throbber'})}
      />
    );
  }

  return (
    <div className={css(styles.editorPage)} {...getTestProps({component: 'MethodEditor', element: 'root'})}>
      <div className={css(styles.editorBody)}>
        <MethodEditorContext.Provider value={methodEditorContextValue}>
          <TabContainer<MethodEditorTab>
            className={classNames(
              'u-flex-grow u-min-width-zero tabContainer--compact',
              css(styles.tabContainer)
            )}
            headerDividerClassName={css(styles.tabContainerHeaderDividerBorder)}
            activeTab={activeMethodEditorTab}
            onRequestTabChange={setCurrentTab}
            paneClassName={classNames(
              css(styles.tabPane),
              activeMethodEditorTab === MethodEditorTab.STEP_CONFIG && css(styles.noScroll),
              // Flex layout for tabs that have sticky submit/cancel button headers & scrollable body
              [MethodEditorTab.DEFINITIONS, MethodEditorTab.TESTS].includes(activeMethodEditorTab) &&
                css(styles.flexTabPane)
            )}
          >
            <Tab<MethodEditorTab>
              tabKey={MethodEditorTab.DEFINITIONS}
              title={
                <MethodEditorTabTitle
                  tabTitle={MethodEditorTab.DEFINITIONS.valueOf()}
                  tabRelatedValidationErrors={parameterTableValidationErrors}
                />
              }
            >
              <div className={classNames(css(styles.contentCard), css(styles.contentCardWithHeader))}>
                <MethodDefinitionTables
                  isEditingMethodTab={isEditingMethodTab}
                  isProcedureReadOnly={props.isProcedureReadOnly}
                  editingMethodCid={selectedMethod.cid}
                  selectedMethod={selectedMethod}
                  onChangeMethod={props.onChangeMethod}
                  updateIsEditingState={(newState: boolean) => {
                    setIsEditingMethodTab(newState);
                    doPauseAutosaver(newState);
                  }}
                  navigateToStep={navigateToStepHandler}
                />
              </div>
            </Tab>
            <Tab<MethodEditorTab>
              tabKey={MethodEditorTab.STEP_CONFIG}
              title={
                <MethodEditorTabTitle
                  tabTitle={MethodEditorTab.STEP_CONFIG.valueOf()}
                  tabRelatedValidationErrors={filterProcedureValidationErrors(
                    selectedMethodValidationErrors,
                    {
                      errorTypes: [ProcedureValidationErrorType.INVALID_NAME],
                      primaryObjectType: ProcedureValidationObjectType.STEP,
                    }
                  )}
                />
              }
            >
              <StepConfigEditor
                method={selectedMethod}
                onChangeSteps={onChangeSteps}
                selectedStepCid={selectedStepCid}
                onChangeSelectedStep={onChangeSelectedStep}
                onReorderedStep={onReorderedStep}
                isProcedureReadOnly={props.isProcedureReadOnly}
              />
            </Tab>
            {BenchlingConfig.PROCEDURES_ENABLE_ANALYSIS_TEMPLATE_INTEGRATION && (
              <Tab<MethodEditorTab.ANALYSIS_TEMPLATE_CONFIG>
                tabKey={MethodEditorTab.ANALYSIS_TEMPLATE_CONFIG}
                title={
                  <MethodEditorTabTitle
                    tabTitle={MethodEditorTab.ANALYSIS_TEMPLATE_CONFIG.valueOf()}
                    tabRelatedValidationErrors={[]}
                  />
                }
              >
                <div className={css(styles.contentCard)}>
                  {selectedMethod.id == null ? (
                    <CenteredSpinner />
                  ) : (
                    <AnalysisTemplateConfig
                      method={selectedMethod}
                      onChangeMethod={onChangeMethod}
                      isProcedureReadOnly={props.isProcedureReadOnly}
                      navigateToTests={() => {
                        setCurrentTab(MethodEditorTab.TESTS);
                      }}
                    />
                  )}
                </div>
              </Tab>
            )}
            {procedureType === ProcedureType.ASSAY && BenchlingConfig.SAMPLE_TESTS_ENABLE && (
              <Tab<MethodEditorTab.TESTS>
                tabKey={MethodEditorTab.TESTS}
                title={
                  <MethodEditorTabTitle
                    tabTitle={MethodEditorTab.TESTS.valueOf()}
                    tabRelatedValidationErrors={[
                      ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
                        errorTypes: [
                          ProcedureValidationErrorType.DUPLICATE_TEST_DEFINITION,
                          ProcedureValidationErrorType.DUPLICATE_DILUTION_FACTOR,
                          ProcedureValidationErrorType.INVALID_DILUTION_FACTOR,
                          ProcedureValidationErrorType.DILUTION_PLAN_WITHOUT_TEST_DEFINITIONS,
                          ProcedureValidationErrorType.INVALID_DILUTION_ASSIGNMENT,
                        ],
                      }),
                    ]}
                  />
                }
              >
                <div className={classNames(css(styles.contentCard), css(styles.contentCardWithHeader))}>
                  <TestTablesEditor
                    isEditingMethodTab={isEditingMethodTab}
                    isProcedureReadOnly={props.isProcedureReadOnly}
                    editingMethodCid={selectedMethod.cid}
                    selectedMethod={selectedMethod}
                    onChangeMethod={onChangeMethod}
                    updateIsEditingState={(newState: boolean) => {
                      setIsEditingMethodTab(newState);
                      doPauseAutosaver(newState);
                    }}
                  />
                </div>
              </Tab>
            )}
            <Tab<MethodEditorTab.METADATA>
              tabKey={MethodEditorTab.METADATA}
              title={
                <MethodEditorTabTitle
                  tabTitle={MethodEditorTab.METADATA.valueOf()}
                  tabRelatedValidationErrors={[
                    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
                      errorTypes: [ProcedureValidationErrorType.INVALID_NAME],
                      primaryObjectType: ProcedureValidationObjectType.METHOD,
                    }),
                    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
                      errorTypes: [ProcedureValidationErrorType.NON_UNIQUE_NAME],
                      primaryObjectType: ProcedureValidationObjectType.METHOD,
                    }),
                  ]}
                />
              }
            >
              <div className={css(styles.contentCard)}>
                <MethodMetadataEditor
                  selectedMethod={selectedMethod}
                  methodNamesInUse={allMethodNamesExcludingSelectedMethod}
                  onChangeMethod={props.onChangeMethod}
                  isProcedureReadOnly={props.isProcedureReadOnly}
                />
              </div>
            </Tab>
          </TabContainer>
        </MethodEditorContext.Provider>
      </div>
    </div>
  );
}

const borderStyle = `1px solid ${GRAY}`;
const styles = StyleSheet.create({
  editorPage: {
    width: '100%',
    height: '100%',
    background: WHITE,
  },
  editorBody: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
  },
  sidebarWrapper: {
    height: '100%',
    borderRight: borderStyle,
  },
  tabContainer: {
    height: '100%',
    marginTop: '-4px',
  },
  tabPane: {
    // We need to subtract 40px because that's the height of the header with the tab names
    // If we didn't subtract anything, you can't scroll all the way to the bottom of the tab content
    height: 'calc(100% - 40px)',
    overflowY: 'scroll',
    marginTop: 0,
  },
  flexTabPane: {
    display: 'flex',
  },
  noScroll: {
    overflowY: 'hidden',
  },
  methodsSidebar: {
    marginRight: '-1px',
  },
  tabLabel: {
    display: 'flex',
    fontSize: '12px',
    paddingTop: '12px',
    paddingBottom: '6px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  templateSelection: {
    marginTop: '12px',
  },
  templateSelectionButtonGroup: {
    display: 'flex',
  },
  warningBanner: {
    marginTop: '12px',
  },
  contentCard: {
    margin: `${methodEditorVerticalMargin} ${methodEditorHorizontalMargin}`,
  },
  // Extra styling for tabs that have sticky headers. Does not work for all the other tabs (causes overflow).
  contentCardWithHeader: {
    overflow: 'hidden',
    width: '100%',
    // Handled inside scroll containers
    marginBottom: '0px',
    marginRight: '0px',
  },
  tabContainerHeaderDividerBorder: {
    borderTop: borderStyle,
  },
});
